import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Box, { BoxProps } from 'ui-box';
import { useDebouncedCallback } from 'use-debounce/lib';
import {
  Button,
  ControlGroup,
  EmptyInputGroup,
  ISelectProps,
  majorScale,
  minorScale,
  Select,
  Spinner,
} from '../../npaper';
import {
  IContact,
  IContactSnapshotIn,
  IContactSnapshotOut,
  useStores,
} from '../../stores';
import { IPaginationFilter } from '../../stores/utils/modelWithCRUD';
import { ContactThumbnail } from './ContactThumbnail';

export interface IContactSelectProps extends ISelectProps<IContact> {
  // query?: string;
  // children?: ReactNode;
  // placeholder?: string;
  // disabled?: boolean;
  // title?: string;
  // onSelect: (item: IContact) => void;
  // selected?: IContact['id'];
  // small?: boolean;
  // onOpen?: () => void;
  // hideSearchInput?: boolean;
  // closesOnSelect?: boolean;
  filter?: IPaginationFilter<IContactSnapshotIn>['filter'];
}

export const ContactSelect: React.FC<IContactSelectProps> = observer(
  ({
    children,
    query: propQuery = '',
    placeholder: defaultPlaceholder,
    title,
    selected = null,
    onSelect,
    filter = {},
    disabled = false,
    small,
    onOpen,
    hideSearchInput = false,
    closesOnSelect = true,
    placement,
  }) => {
    const intl = useIntl();
    const placeholder =
      defaultPlaceholder ||
      intl.formatMessage({ defaultMessage: 'Choose a contact' });

    const { contactStore } = useStores();

    const [opened, setOpened] = useState(false);
    const [innerSelected, setInnerSelected] = useState<IContact | null>(null);

    useEffect(() => {
      let isSub = true;
      if (selected) {
        contactStore
          .getItem(selected)
          .then((comp) => isSub && setInnerSelected(comp));
      }
      return () => {
        isSub = false;
      };
    }, [contactStore, selected]);

    const [items, setItems] = useState<IContact[]>();
    const [query, setQuery] = useState(propQuery);
    const debouncedQuery = useDebouncedCallback(setQuery, 150);

    useEffect(() => {
      debouncedQuery.callback(propQuery);
    }, [debouncedQuery, propQuery]);

    useEffect(() => {
      if (!opened) return;

      let isSub = true;
      contactStore
        .fetchMore({ filter: { ...filter, search: query } })
        .then((res) => isSub && setItems(res.items));

      return () => {
        isSub = false;
      };
    }, [contactStore, filter, query, opened]);

    return (
      <Select
        placement={placement}
        closesOnSelect={closesOnSelect}
        title={title}
        placeholder={placeholder}
        disabled={disabled}
        hideSearchInput={hideSearchInput}
        isOpen={opened}
        onOpen={() => {
          setOpened(true);
          onOpen && onOpen();
        }}
        onClose={() => {
          setOpened(false);
        }}
        small={small}
        onQueryChange={debouncedQuery.callback}
        items={items}
        isLoading={!items || contactStore.isLoading}
        resolveSelectedValue={(id) => contactStore.getItem(id)}
        itemRenderer={(item) => <ContactSelectItemRenderer contact={item} />}
        onSelect={(contact) => {
          setInnerSelected(contact);
          onSelect && onSelect(contact);
        }}
        value={innerSelected ? innerSelected.id : undefined}
        valueRenderer={(selected) => selected.name}
      >
        {children}
      </Select>
    );
  }
);

export const ContactSelectItemRenderer = ({
  contact,
}: {
  contact: IContact;
}) => (
  <>
    <ContactThumbnail contact={contact} showName={false} noLink />
    <Box marginLeft={majorScale(1)}>
      <p>{contact.name}</p>
      {contact.emails && contact.emails.length > 0 && (
        <Box className="p muted small">
          <FormattedMessage
            defaultMessage="{email} {numEmails, plural, one {} other {and # more}}"
            values={{
              email: contact.emails[0],
              numEmails: contact.emails.length,
            }}
          />
        </Box>
      )}
    </Box>
  </>
);

export interface IContactsInputProps
  extends Omit<
    Omit<Omit<BoxProps<'div'>, 'onChange'>, 'onBlur'>,
    'defaultValue'
  > {
  value?: IContactSnapshotOut['id'][];
  defaultValue?: IContactSnapshotOut['id'][];
  onChange?: (contacts: IContact['id'][]) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  filter?: IPaginationFilter<IContactSnapshotOut>['filter'];
}

export const ContactsInput: React.FC<IContactsInputProps> = ({
  placeholder = 'Tilføj kontaktperson',
  value,
  defaultValue = [],
  onChange,
  onBlur,
  filter = {},
  ...props
}) => {
  const addContact = (contact: IContact) => {
    const contacts = Array.from(value || []);
    const index = contacts.findIndex((c) => c === contact.id);
    if (index === -1) {
      contacts.push(contact.id);
      onChange && onChange(contacts);
    }
  };

  const removeContact = (index?: number) => {
    if (!value) return;
    const contacts = Array.from(value);
    if (contacts.length === 0) return;
    if (index === undefined) {
      contacts.splice(contacts.length - 1, 1);
    } else {
      contacts.splice(index, 1);
    }
    onChange && onChange(contacts);
  };

  return (
    <Box marginBottom={majorScale(1)}>
      {(value || defaultValue) &&
        (value || defaultValue).map((id, i) => (
          <ContactItem
            contactId={id}
            key={id}
            onRemove={() => removeContact(i)}
          />
        ))}
      <ContactSelect
        onSelect={addContact}
        filter={{ ...filter, id__exclude: value }}
      >
        <Button iconLeft="add">{placeholder}</Button>
      </ContactSelect>
    </Box>
  );
};

const ContactItem = ({
  contactId,
  onRemove,
}: {
  contactId: IContact['id'];
  onRemove: () => void;
}) => {
  const [contact, setContact] = useState<IContact>();
  const { contactStore } = useStores();
  useEffect(() => {
    let isSub = true;
    contactStore
      .getItem(contactId)
      .then((contact) => isSub && setContact(contact));
    return () => {
      isSub = false;
    };
  }, [contactId, contactStore]);
  return contact ? (
    <ControlGroup fill marginBottom={minorScale(1)}>
      <EmptyInputGroup width="100%">{contact.name}</EmptyInputGroup>
      <Button height={32} iconLeft="cross" onClick={() => onRemove()} />
    </ControlGroup>
  ) : (
    <Spinner size={16} />
  );
};
